<template>
    <div>
        <div class="row">
            <div class="col-xl-3 col-lg-6 col-sm-12 mb-2">
                <div class="p-0">
                    <label>{{ $t('table.isometrics') }}</label>
                </div>
                <div class="col-lg-12 p-0">
                    <multiselect v-model="selectedIsometrics"
                        :options="currentIsometrics"
                        :multiple="true"
                        :clear-on-select="true"
                        :hide-selected="false"
                        :loading="isIsometricsLoading"
                        :preserve-search="true"
                        placeholder=""
                        label="info" track-by="id"
                        :selectLabel="''"
                        @search-change="onIsometricsSearch"
                        @input="updateIsometrics"
                    >
                        <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                        <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                    </multiselect>
                </div>
            </div>

            <div class="col-xl-3 col-lg-6 col-sm-12 mb-2">
                <div class="p-0">
                    <label>{{ $t('table.calculation_method') }}</label>
                </div>
                <div class="col-lg-12 p-0">
                    <multiselect v-model="selectedCalculationMethod"
                        :options="isometric_calculation_methods"
                        :multiple="false"
                        :clear-on-select="true"
                        :hide-selected="false"
                        placeholder=""
                        label="name" track-by="code"
                        :selectLabel="''"
                        @input="updateCalculationMethod"
                    >
                        <template slot="clear" slot-scope="props">
                            <div
                                v-if="selectedCalculationMethod != null"
                                class="multiselect__clear"
                                @mousedown.prevent.stop="clearSelectedCalculationMethod"
                            >
                            </div>
                        </template>
                        <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                        <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                    </multiselect>
                </div>
            </div>

            <div class="col-xl-3 col-lg-6 col-sm-12 mb-2">
                <div class="p-0">
                    <label>{{ $t('table.consider') }}</label>
                </div>
                <div class="col-lg-12 p-0">
                    <multiselect v-model="selectedConsideration"
                        :options="isometric_consideration"
                        :multiple="false"
                        :clear-on-select="true"
                        :hide-selected="false"
                        placeholder=""
                        label="name" track-by="code"
                        :selectLabel="''"
                    >
                        <template slot="clear" slot-scope="props">
                            <div
                                v-if="selectedConsideration != null"
                                class="multiselect__clear"
                                @mousedown.prevent.stop="clearSelectedConsideration"
                            >
                            </div>
                        </template>
                        <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                        <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                    </multiselect>
                </div>
            </div>

            <div class="col-xl-3 col-lg-6 col-sm-12 mb-2">
                <div class="p-0">
                    <label>{{ $t('table.isometrics_associated_to_pressure_tests') }}</label>
                </div>
                <div class="col-lg-12 p-0">
                    <multiselect v-model="selectedPressureTestIsometrics"
                        :options="currentPressureTestIsometrics"
                        :multiple="true"
                        :clear-on-select="true"
                        :hide-selected="false"
                        :loading="isPressureTestIsometricsLoading"
                        :preserve-search="true"
                        placeholder=""
                        label="test_circuit_no" track-by="id"
                        :selectLabel="''"
                        @search-change="onPressureTestIsometricsSearch"
                        @input="updatePressureTestIsometrics"
                    >
                        <span slot="noOptions">{{ $t('modal.general.list_is_empty') }}</span>
                        <span slot="noResult">{{ $t('modal.general.no_results') }}</span>
                    </multiselect>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-xl-2 col-lg-6 col-sm-12 mb-2">
                <div class="p-0">
                    <label>{{ $t('table.based_on_priority') }}</label>
                </div>
                <div class="col-lg-12 p-0">
                    <toggle-button v-model="post_data.based_on_priority" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                </div>
            </div>

            <div class="col-xl-2 col-lg-6 col-sm-12 mb-2" v-if="basedOnDIAInch">
                <div class="p-0">
                    <label>{{ $t('table.consider_only_but_welds') }}</label>
                </div>
                <div class="col-lg-12 p-0">
                    <toggle-button v-model="post_data.consider_but_welds" :sync="true" :value="false" :labels="{checked: $t('misc.yes'), unchecked: $t('misc.no')}"/>
                </div>
            </div>

            <div class="col-lg-12 mt-2">
                <b-btn class="btn btn-success float-left" @click="calculateBestPrefabrication">
                    <i class="fa fa-plus"></i> {{$t('general.buttons.calculate')}}
                </b-btn>
                <b-btn class="btn btn-danger float-right text-white" @click="clearFilters">
                    <i class="fa fa-times-circle-o"></i> {{$t('general.buttons.clear')}}
                </b-btn>
            </div>
        </div>

        <hr>

        <data-table
            :exportable="false" title=""
            :search="false"
            :rows="getData.rowData"
            :columns="columnData"
            :paginate="false"
        >
        </data-table>

        <b-btn class="btn btn-dark mt-2" @click="returnToIsometric">
            <i class="fa fa-arrow-left" aria-hidden="true"></i> {{$t('general.buttons.return_to.return_to_isometric_management')}}
        </b-btn>
    </div>
</template>

<script>
import Vue from 'vue';
import http from '@/libs/http';
import DataTable from '@/components/global/tables/datatables/DataTable.vue';
import miniToastr from 'mini-toastr';
import * as notificationIcons from '@/components/global/miscellaneous/notificationIcons.js';
import Multiselect from 'vue-multiselect';

export default {
    name: 'isometric_management_best_prefabrication',
    components: {
        DataTable,
        notificationIcons,
        Multiselect,
    },
    props: {
        weldPlace: {
            type: String,
            required: true,
        },
        columnData: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    data() {
        return {
            breadcrumbItems: [
                {
                    text: Vue.i18n.translate('general.dashboard.title'),
                    to: '/'
                },
                {
                    text: Vue.i18n.translate('general.projects.title_modal') + ': ' + this.$store.state.current_project.name,
                    to: '/project/ '+ this.$route.params.id,
                },
                {
                    text: Vue.i18n.translate('general.isometric_management.title'),
                    to: '/project/' + this.$route.params.id + '/isometric_management',
                },
                {
                    text: Vue.i18n.translate('general.isometric_management.best_prefabrication.calculate_title'),
                    to: '/project/' + this.$route.params.id + '/isometric_management',
                },
            ],

            basedOnDIAInch: false,

            post_data: {
                project_id: null,
                isometrics: [],
                calculation_method: null,
                isometrics_associated_to_pressure_tests: false,
                consideration: null,
                pressure_test_isometrics: [],
                based_on_priority: false,
                consider_but_welds: false,
            },

            selectedIsometrics: [],
            currentIsometrics: [],
            isIsometricsLoading: false,

            selectedPressureTestIsometrics: [],
            currentPressureTestIsometrics: [],
            isPressureTestIsometricsLoading: false,

            selectedConsideration: null,
            selectedCalculationMethod: null,

            getData: {
                rowData: [],
            },
        }
    },
    mounted() {
    },
    computed: {
        isometric_consideration: {
            get: function () {
                let isometric_consideration = this.$store.getters.getIsometricConsideration;

                if (typeof isometric_consideration != 'undefined' && isometric_consideration != null) {
                    isometric_consideration = Object.values(isometric_consideration);
                } else {
                    isometric_consideration = [];
                }

                return isometric_consideration;
            }
        },
        isometric_calculation_methods: {
            get: function () {
                let isometric_calculation_methods = this.$store.getters.getIsometricCalculationMethods;

                if (typeof isometric_calculation_methods != 'undefined' && isometric_calculation_methods != null) {
                    isometric_calculation_methods = Object.values(isometric_calculation_methods);
                } else {
                    isometric_calculation_methods = [];
                }

                return isometric_calculation_methods;
            }
        },
    },
    watch: {
        isometric_consideration: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        },
        isometric_calculation_methods: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
            }
        },
    },
    methods: {
        successMsg(msg) {
            miniToastr.success(msg, Vue.i18n.translate('messages.success'), 5000);
        },
        errorMsg(msg) {
            miniToastr.error(msg, Vue.i18n.translate('messages.error'), 5000);
        },
        infoMsg(msg) {
            miniToastr.info(msg, Vue.i18n.translate('messages.info'), 4000);
        },
        calculateBestPrefabrication () {
            this.$emit('showBarLoader', true);
            this.post_data.isometrics = [];
            this.post_data.pressure_test_isometrics = [];

            //Add the weld_place
            this.post_data.weld_place = this.weldPlace;

            //Add the project_id
            this.post_data.project_id = this.$route.params.id;

            //Only send the IDs
            this.selectedIsometrics.forEach((field, idx) => {
                this.post_data.isometrics[idx] = field.id;
            });
            this.selectedPressureTestIsometrics.forEach((field, idx) => {
                this.post_data.pressure_test_isometrics[idx] = field.id;
            });

            this.post_data.consideration = (this.selectedConsideration != null) ? this.selectedConsideration.code : null;
            this.post_data.calculation_method = (this.selectedCalculationMethod != null) ? this.selectedCalculationMethod.code : null;

            let data = http.route('drawing/calculate_best_isometric_prefabrication').post(this.post_data, true);
            data.then(response => {
                if(response.isError == false) {
                    if (this.basedOnDIAInch) {
                        switch(this.weldPlace) {
                            case 'sw':
                                this.columnData[1] = {
                                    label: Vue.i18n.translate('table.total_dia_inch_prefabrication'),
                                    field: 'total_dia_inch_prefabrication',
                                    numeric: false,
                                    html: false,
                                };
                            break;
                            case 'fw':
                                this.columnData[1] = {
                                    label: Vue.i18n.translate('table.total_dia_inch_erection'),
                                    field: 'total_dia_inch_erection',
                                    numeric: false,
                                    html: false,
                                };
                            break;
                        }
                    } else {
                        this.columnData[1] = {
                            label: Vue.i18n.translate('table.total_weight'),
                            field: 'total_weights',
                            numeric: false,
                            html: false,
                        };
                    }

                    this.getData.rowData = response.data;
                    this.infoMsg(Vue.i18n.translate('messages.table_filtered'));
                } else {
                    Object.keys(response.errorMessage).forEach(key => {
                        this.errorMsg(Vue.i18n.translate(response.errorMessage[key]));
                    });
                }

                this.$emit('showBarLoader', false);
            });
        },
        clearFilters () {
            this.selectedIsometrics = [];
            this.selectedCalculationMethod = null;
            this.selectedPressureTestIsometrics = [];
            this.selectedConsideration = null;

            this.post_data = {
                isometrics: [],
                calculation_method: null,
                pressure_test_isometrics: [],
                consideration: null,
                consider_but_welds: false,
                based_on_priority: false,
            };

            this.getData.rowData = [];

            this.infoMsg(Vue.i18n.translate('messages.filters_cleared'));
        },
        returnToIsometric () {
            this.$router.push('/project/'+this.$route.params.id+'/isometric_management');
        },
        //Isometrics Search
        onIsometricsSearch(search) {
            if (search == '') {
                this.currentIsometrics = [];

                return false;
            }

            this.isIsometricsLoading = true;
            this.searchIsometrics(search, this);
        },
        searchIsometrics: _.debounce((search, vm) => {
            let data = http.route('drawing?filters[parent_drawing_id]=null&project_id=' + vm.$route.params.id + '&search=' + search + '&filters[discipline_id]=1').get();
            data.then(response => {
                if (response.data != false) {
                    vm.currentIsometrics = response.data;
                }
                vm.isIsometricsLoading = false;
            });
        }, 500),
        updateIsometrics(isometric) {
        },
        //Pressure Test Isometrics Search
        onPressureTestIsometricsSearch(search) {
            if (search == '') {
                this.currentPressureTestIsometrics = [];

                return false;
            }

            this.isPressureTestIsometricsLoading = true;
            this.searchPressureTestIsometrics(search, this);
        },
        searchPressureTestIsometrics: _.debounce((search, vm) => {
            let data = http.route("pressuretest?project_id="+vm.$route.params.id+"&filters[parent_pressure_test_id]=null&get_all=1&search_select=" + search).get()
            data.then(response => {
                if (response.data != false) {
                    vm.currentPressureTestIsometrics = response.data;
                }
                vm.isPressureTestIsometricsLoading = false;
            });
        }, 500),
        updatePressureTestIsometrics(isometric) {
        },
        //Consideration
        updateConsideration (consideration) {
        },
        clearSelectedConsideration () {
            this.selectedConsideration = null;
        },
        //Calculation Method
        updateCalculationMethod(calculation_method) {
            this.basedOnDIAInch = (calculation_method != null && calculation_method.code == 'based_on_dia_inch');
        },
        clearSelectedCalculationMethod () {
            this.selectedCalculationMethod = null;
            this.basedOnDIAInch = false;
        }
    },
}
</script>

<style scoped>
    select {
        -webkit-appearance:menulist-button;
        -moz-appearance:menulist-button;
    }
</style>


// WEBPACK FOOTER //
// src/components/pages/project/isometric_management/calculation/isometric_management_best.vue